import React, { useState, useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'
import { fetchProgramsForParams } from '../../api/programs'
import { deriveBaselines, computeModel } from '../../utils/breakEvenModel'
import { DEFAULT_SETTINGS, loadSettings, saveSettings } from '../../constants/breakEven'
import BreakEvenInputs from './BreakEvenInputs'
import BreakEvenChart from './BreakEvenChart'
import BreakEvenSummaryCard from './BreakEvenSummaryCard'
import Loader from '../common/Loader'
import './BreakEven.css'

const SEASON_CYCLE = ['winter', 'spring', 'summer', 'fall']
const SEASON_LABEL = { winter: 'Winter', spring: 'Spring', summer: 'Summer', fall: 'Fall' }
const SEASONS_SHOWN = 7

function currentSeason(now = DateTime.now()) {
  const m = now.month
  if (m <= 2 || m === 12) return { year: m === 12 ? now.year + 1 : now.year, season: 'winter' }
  if (m <= 5) return { year: now.year, season: 'spring' }
  if (m <= 8) return { year: now.year, season: 'summer' }
  return { year: now.year, season: 'fall' }
}

/** The last `count` seasons ending one season ahead of today, chronologically. */
export function recentSeasons(count, now = DateTime.now()) {
  const { year, season } = currentSeason(now)
  let idx = SEASON_CYCLE.indexOf(season) + 1
  let y = year
  if (idx > 3) { idx = 0; y += 1 }
  const out = []
  for (let i = 0; i < count; i += 1) {
    out.unshift({ year: y, season: SEASON_CYCLE[idx] })
    idx -= 1
    if (idx < 0) { idx = 3; y -= 1 }
  }
  return out
}

const money = (n) => `${n < 0 ? '−' : ''}$${Math.abs(Math.round(n)).toLocaleString()}`
const signed = (n) => `${n >= 0 ? '+' : '−'}$${Math.abs(Math.round(n)).toLocaleString()}`
const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0)

function BreakEven() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [seasonData, setSeasonData] = useState([])
  const [settings, setSettings] = useState(loadSettings)

  useEffect(() => { saveSettings(settings) }, [settings])

  useEffect(() => {
    let cancelled = false
    async function load() {
      const wanted = recentSeasons(SEASONS_SHOWN)
      try {
        const results = await Promise.all(
          wanted.map(async ({ year, season }) => {
            const res = await fetchProgramsForParams({ year: String(year), season })
            // The API wraps payloads as { status, message, data }.
            const programs = Array.isArray(res) ? res : (res && res.data) || []
            // This endpoint silently ignores unrecognized params and will happily
            // return the entire corpus, so verify what came back matches the ask.
            const matching = programs.filter(
              (p) => Number(p.year) === year && p.season === season
            )
            return {
              key: `${year}_${season}`,
              label: SEASON_LABEL[season],
              yearShort: `'${String(year).slice(2)}`,
              year,
              season,
              programs: matching,
            }
          })
        )
        if (!cancelled) { setSeasonData(results); setLoading(false) }
      } catch (e) {
        if (!cancelled) { setError('Could not load program history.'); setLoading(false) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const baselines = useMemo(
    () => (seasonData.length ? deriveBaselines(seasonData) : null),
    [seasonData]
  )

  const view = useMemo(() => {
    if (!baselines || !baselines.class) return null

    const seasons = baselines.seasons.map((s) => ({
      ...s,
      yearShort: `'${String(s.year).slice(2)}`,
    }))
    // Only the recent window is representative — older seasons come from a
    // differently-shaped business and drag the run rate down.
    const schoolYearRuns = seasons
      .filter((s) => !s.summer && s.ran !== null)
      .slice(-3)
      .map((s) => s.ran)
    const summerSeasons = seasons.filter((s) => s.summer && s.ran !== null)
    const runsSchoolYear = Math.round(mean(schoolYearRuns))
    const runsSummer = summerSeasons.length ? summerSeasons[summerSeasons.length - 1].ran : 0

    const camp = baselines.camp || baselines.class
    // Royalty on gross is exactly equivalent to a higher rate applied to net,
    // since gross = net / (1 - revShare). One rate, one code path.
    const royaltyRate = settings.royaltyOnGross && baselines.revShare < 1
      ? settings.royaltyRate / (1 - baselines.revShare)
      : settings.royaltyRate

    const modelSettings = {
      ...settings,
      royaltyRate,
      revShare: baselines.revShare,
      cancelRate: baselines.cancelRate,
      classEnrollment: baselines.class.enrollment,
      classPrice: baselines.class.price,
      classCoachHours: baselines.class.coachHours,
      campEnrollment: camp.enrollment,
      campPrice: camp.price,
      campCoachHours: camp.coachHours,
    }

    const model = computeModel(modelSettings, runsSchoolYear, runsSummer)
    const latestSummer = summerSeasons[summerSeasons.length - 1]
    const observedCampMix = latestSummer
      ? (seasonData.find((s) => s.key === latestSummer.key)?.programs || [])
          .filter((p) => ['paid', 'complete', 'invoiced', 'active'].includes(p.status))
          .reduce((a, p, _i, arr) => a + (p.type === 'camp' ? 1 / arr.length : 0), 0)
      : 0

    return {
      seasons,
      model,
      runsSchoolYear,
      runsSummer,
      railBaselines: {
        class: baselines.class,
        camp: baselines.camp,
        revShare: baselines.revShare,
        cancelRate: baselines.cancelRate,
        observedCampMix,
        latestSummerLabel: latestSummer ? latestSummer.yearShort : '—',
      },
    }
  }, [baselines, settings, seasonData])

  if (loading) return <Loader />
  if (error) return <div className="be-error">{error}</div>
  if (!view) return <div className="be-error">No program history available to build a baseline.</div>

  const { model, seasons } = view
  const sy = model.schoolYear
  const su = model.summer

  return (
    <div className="be-page">
      <div className="be-mast">
        <h1>Break-Even Tracker</h1>
        <span className="be-sub">Skyhawks Peninsula · Kinetic Kids, LLC</span>
        <span className="be-stamp">
          live · {baselines.programCount} programs · {seasons.length} seasons
        </span>
      </div>

      <div className="be-shell">
        <BreakEvenInputs
          settings={settings}
          baselines={view.railBaselines}
          fixedTotal={model.fixedPerQuarter}
          onChange={setSettings}
          onReset={() => setSettings({ ...DEFAULT_SETTINGS })}
        />

        <main className="be-main">
          <section className="be-answers">
            <BreakEvenSummaryCard
              title="School-year quarter"
              mix="Fall · Winter · Spring — 100% classes"
              unit="classes must run"
              data={sy}
              actualLabel={`You run ~${view.runsSchoolYear}`}
              offerNote={
                <>Which means offering <b>{sy.offer === null ? '—' : sy.offer}</b> — at{' '}
                  {Math.round(view.railBaselines.cancelRate * 100)}% cancellation</>
              }
            />
            <BreakEvenSummaryCard
              title="Summer quarter"
              mix={`${Math.round(settings.summerCampMix * 100)}% camps · ${Math.round(100 - settings.summerCampMix * 100)}% classes`}
              unit="programs must run"
              data={su}
              actualLabel={`You run ${view.runsSummer}`}
              offerNote={
                <>Which means offering <b>{su.offer === null ? '—' : su.offer}</b> — camps carry{' '}
                  <b>{model.campMultiple ? `${model.campMultiple.toFixed(1)}×` : '—'}</b> the contribution</>
              }
            />
          </section>

          <section className="be-panel">
            <h2>Programs run against the break-even line</h2>
            <div className="be-note">
              The line steps down in summer, when camps raise contribution per program.
              Seasons still in registration are shown dashed and are not scored.
            </div>
            <BreakEvenChart
              seasons={seasons}
              breakEvenSchoolYear={sy.breakEven}
              breakEvenSummer={su.breakEven}
            />
            <div className="be-legend">
              <span><i className="be-sw be-bg-good" />Cleared break-even</span>
              <span><i className="be-sw be-bg-short" />Below break-even</span>
              <span><i className="be-sw be-bg-pending" />Pending — in registration</span>
              <span><i className="be-sw be-bg-line" />Break-even line</span>
            </div>
          </section>

          <section className="be-annual">
            <div className="be-panel">
              <h2>Annual roll-up</h2>
              <div className="be-note">
                Per-quarter break-even misleads when one quarter funds three.
              </div>
              <table className="be-rollup">
                <tbody>
                  <tr>
                    <td>3 × school-year quarter</td>
                    <td className={model.annual.schoolYearTotal >= 0 ? 'be-pos' : 'be-neg'}>
                      {signed(model.annual.schoolYearTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td>Summer quarter</td>
                    <td className={model.annual.summerTotal >= 0 ? 'be-pos' : 'be-neg'}>
                      {signed(model.annual.summerTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td>Net before owner compensation</td>
                    <td className={model.annual.net >= 0 ? 'be-pos' : 'be-neg'}>
                      {signed(model.annual.net)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="be-note be-spaced">
                Contribution per program run — <b>{money(model.contributionClass)}</b> class ·{' '}
                <b>{money(model.contributionCamp)}</b> camp. Fixed costs{' '}
                <b>{money(model.fixedPerQuarter)}</b> per quarter before royalty.
              </div>
            </div>

            <div className="be-flags">
              <div className="be-flag be-f-short">
                <b>Advertising floor.</b> The FDD requires {Math.round(settings.localAdRate * 1000) / 10}% of
                gross revenue on local marketing. Budgets below the floor are lifted to it automatically.
              </div>
              <div className="be-flag be-f-accent">
                <b>Royalty reconciliation.</b> The minimum is a non-refundable floor, so a yearly
                true-up lets summer absorb the school-year shortfall. Toggle it to see the difference.
              </div>
              <div className="be-flag be-f-pending">
                <b>Coach wages are the open question.</b> Modeled wages ran 1.75× actuals for
                Mar–May 2026, most likely coaches covering concurrent classes. The calibration
                slider spans both worlds.
              </div>
            </div>
          </section>

          <div className="be-prov">
            <b>PROVENANCE</b><br />
            enrollment, price, rev share, cancellation, program counts — Skyhawks API{' '}
            <b>/fetch_programs_for_params</b>, {seasons.length} seasons,{' '}
            {baselines.programCount} programs, {baselines.ranCount} ran<br />
            royalty, minimum, tech fee, advertising floor, camp shirt requirement —{' '}
            <b>Skyhawks 2023 FDD Review Sheet</b><br />
            fixed cost defaults — <b>kinetic-kids-llc-profit-loss-20260620.xlsx</b>, TTM Jul 2025–Jun 2026<br />
            excluded — one-time franchise fee (Sep 2025, new territory) · coach incentives ·
            customer-paid $5 processing fee
          </div>
        </main>
      </div>
    </div>
  )
}

export default BreakEven
